import { useEffect, useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { shortlists as api, ApiError, type Shortlist } from '@/lib/api';
import { useShortlistStore, type LocalShortlist } from './store';

/**
 * Bidirectional sync для shortlist:
 *   1) При логине — pull с сервера, мёрджим в локальный store. Локальные list с localOnly=true
 *      загружаются как новые на сервер.
 *   2) При локальных изменениях (dirty=true) — push с debounce 800ms.
 *
 * При офлайне или 401 — silent skip (dirty останется), повторим попытку при следующем триггере.
 */

// A list "signature" for matching local-only ↔ remote и для устранения дублей
// одинаковых списков на сервере. Достаточно стабильна: одинаковое имя + те же
// сохранённые istat (нормализованный порядок).
function signature(name: string, countryCode: string, istats: string[]): string {
  const sortedIstats = [...istats].sort().join(',');
  return `${countryCode}|${name.trim().toLowerCase()}|${sortedIstats}`;
}

export function useShortlistSync() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const lists = useShortlistStore((s) => s.lists);
  const hydrated = useShortlistStore((s) => s.hydrated);

  const lastUserRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef(false);

  // PULL on login
  useEffect(() => {
    if (!hydrated) return;
    if (!userId) {
      lastUserRef.current = null;
      return;
    }
    if (lastUserRef.current === userId) return;
    lastUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        const remoteRaw = await api.list();
        if (cancelled) return;

        // 1) Cleanup: на сервере уже могут лежать дубли (один и тот же
        //    список загружен много раз — старый баг в этом самом эффекте).
        //    Оставляем самый свежий по updatedAt в каждой группе, остальные
        //    удаляем. Группируем по полной сигнатуре (имя + страна + istats).
        const groups = new Map<string, Shortlist[]>();
        for (const r of remoteRaw) {
          const sig = signature(r.name, r.countryCode, r.comuneIstats);
          const arr = groups.get(sig) ?? [];
          arr.push(r);
          groups.set(sig, arr);
        }
        const remote: Shortlist[] = [];
        const toDelete: string[] = [];
        for (const arr of groups.values()) {
          if (arr.length === 1) {
            remote.push(arr[0]);
            continue;
          }
          arr.sort((a, b) => b.updatedAt - a.updatedAt);
          remote.push(arr[0]);
          for (let i = 1; i < arr.length; i++) toDelete.push(arr[i].id);
        }
        // Удаление дублей идёт в фоне; ошибки тихо игнорируем.
        for (const id of toDelete) {
          api.delete(id).catch(() => undefined);
        }

        // 2) Заливаем localOnly на сервер — но сначала проверяем, нет ли
        //    уже точно такого же списка на сервере (по сигнатуре). Если
        //    есть — присваиваем серверный id, ничего не создаём.
        const initial = useShortlistStore.getState();
        const localOnly = initial.lists.filter((l) => l.localOnly);
        const remoteBySig = new Map<string, Shortlist>();
        for (const r of remote) {
          remoteBySig.set(signature(r.name, r.countryCode, r.comuneIstats), r);
        }
        for (const l of localOnly) {
          if (cancelled) return;
          const sig = signature(l.name, l.countryCode, l.comuneIstats);
          const match = remoteBySig.get(sig);
          if (match) {
            // Уже есть — просто привязываемся к серверному id.
            useShortlistStore.getState().markClean(l.id, { id: match.id });
            continue;
          }
          try {
            const created = await api.create({
              name: l.name,
              countryCode: l.countryCode,
              comuneIstats: l.comuneIstats,
              notes: l.notes,
            });
            useShortlistStore.getState().markClean(l.id, { id: created.id });
            // Считаем теперь этот id «существующим на сервере», чтобы в этой же
            // итерации второй такой же localOnly не создал новую копию.
            const dto: Shortlist = {
              id: created.id,
              name: l.name,
              countryCode: l.countryCode,
              comuneIstats: l.comuneIstats,
              notes: Object.keys(l.notes).length > 0 ? l.notes : null,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };
            remote.push(dto);
            remoteBySig.set(sig, dto);
          } catch {
            // silent; повторим в следующий sync
          }
        }
        if (cancelled) return;

        // 3) Финальный merge — берём актуальное состояние стора (там уже
        //    проставлены серверные id у бывших localOnly), сохраняем dirty
        //    локальные правки и любые оставшиеся localOnly (например, после
        //    сетевой ошибки выше).
        const current = useShortlistStore.getState();
        const dirtyMap = new Map(
          current.lists.filter((l) => l.dirty && !l.localOnly).map((l) => [l.id, l]),
        );
        const merged: LocalShortlist[] = remote.map((r) => {
          const dirty = dirtyMap.get(r.id);
          if (dirty) return dirty;
          return {
            id: r.id,
            name: r.name,
            countryCode: r.countryCode,
            comuneIstats: r.comuneIstats,
            notes: r.notes ?? {},
            createdAt: r.createdAt,
            updatedAt: r.updatedAt,
            dirty: false,
            localOnly: false,
          };
        });
        const stillLocal = current.lists.filter((l) => l.localOnly);
        useShortlistStore.setState({ lists: [...stillLocal, ...merged] });
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return;
        // network error — silent
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, hydrated]);

  // PUSH on local change (dirty lists)
  useEffect(() => {
    if (!hydrated || !userId) return;
    const dirtyServerLists = lists.filter((l) => l.dirty && !l.localOnly);
    if (dirtyServerLists.length === 0) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (inflightRef.current) return;
      inflightRef.current = true;
      try {
        for (const l of dirtyServerLists) {
          try {
            await api.update(l.id, {
              name: l.name,
              countryCode: l.countryCode,
              comuneIstats: l.comuneIstats,
              notes: Object.keys(l.notes).length > 0 ? l.notes : null,
            });
            useShortlistStore.getState().markClean(l.id);
          } catch (e) {
            if (e instanceof ApiError && e.status === 404) {
              // Был удалён на сервере — просто удалим локально
              useShortlistStore.getState().remove(l.id);
            }
            // прочие — silent retry next change
          }
        }
      } finally {
        inflightRef.current = false;
      }
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [lists, hydrated, userId]);
}
